// DOWNLOAD THE DELIVERABLES LOCALLY, beside Upload to Procore / ACC (owner,
// 2026-11-04: "add a button next to upload to procore/acc to also download
// locally for deliverables on issues").
//
// The same two options the shop drawings bulk download asks, because it is the
// same question and the owner already chose the answers there: separate files
// in a zip, or one PDF. No register page - an issue's deliverables have no
// register to put on top (see utils/issueDeliverableBundle).
//
// WHAT COULD NOT BE INCLUDED IS SAID, never silently dropped: in the message,
// in the zip's NOT INCLUDED.txt, and on the combined PDF's last page. A
// download that quietly contains six of nine sheets is worse than one that
// refuses, because the person believes they have all nine.
import React, { useState } from "react";
import { Button, Modal, Radio, Tooltip, message } from "antd";
import { DownloadOutlined } from "@ant-design/icons";
import { downloadIssueDeliverables } from "api/issue/issueApi";

/** What the message says after a download: how many files, and every reason one is absent. */
export function downloadMessage(r, mode) {
  const what = mode === "combined" ? "One PDF" : "Zip";
  // "downloading": the browser's own download bar takes over from here.
  const base = `${what} downloading - ${r.included} of ${r.count} deliverable${r.count === 1 ? "" : "s"}`;
  if (!r.missing) return { level: "success", text: base };
  return {
    level: "warning",
    text: `${base}. ${r.missing} not included${r.missingNames ? `: ${r.missingNames.split(" | ").join(", ")}` : ""} - the reasons are ${mode === "combined" ? "on the last page" : "in NOT INCLUDED.txt"}.`,
  };
}

export default function DeliverableDownloadButton({ issueId, hasDeliverables }) {
  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState("separate");
  const [busy, setBusy] = useState(false);

  const go = async () => {
    setBusy(true);
    try {
      const r = await downloadIssueDeliverables(issueId, mode);
      const m = downloadMessage(r, mode);
      message[m.level](m.text, m.level === "warning" ? 8 : 4);
      setOpen(false);
    } catch (e) {
      message.error(e.message || "Could not build the download");
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <Tooltip title={hasDeliverables
        ? "Download this issue's deliverables to your computer"
        : "Nothing has been delivered on this issue yet"}
      >
        <Button
          size="small"
          icon={<DownloadOutlined />}
          disabled={!hasDeliverables}
          onClick={() => setOpen(true)}
          data-testid="issue-deliverable-download"
        >
          Download
        </Button>
      </Tooltip>
      <Modal
        open={open}
        title="Download the deliverables"
        okText="Download"
        okButtonProps={{ loading: busy, "data-testid": "issue-deliverable-download-go" }}
        onOk={go}
        onCancel={() => { if (!busy) setOpen(false); }}
        destroyOnClose
      >
        <Radio.Group value={mode} onChange={(e) => setMode(e.target.value)} style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          <Radio value="separate" data-testid="issue-deliverable-mode-separate">
            <b>Separate files</b> (zip)
            <div style={{ color: "#667085", fontSize: 12, marginLeft: 24 }}>
              One file per deliverable, named as it is named on the issue. Any type.
            </div>
          </Radio>
          <Radio value="combined" data-testid="issue-deliverable-mode-combined">
            <b>One PDF</b>
            <div style={{ color: "#667085", fontSize: 12, marginLeft: 24 }}>
              Every deliverable's pages, in order. A file that is not a PDF or an image cannot go in one PDF and is listed on the last page.
            </div>
          </Radio>
        </Radio.Group>
        <div style={{ color: "#667085", fontSize: 12, marginTop: 12 }}>
          The same files the deliverables email carries and the Procore / ACC upload sends.
        </div>
      </Modal>
    </>
  );
}
